import React, { useEffect, useMemo, useState } from 'react';
import { Spinner, Text } from '@SBGSports/catapult-core';
import { subDays } from 'date-fns';
import { useTranslations } from 'i18n';
import mixpanel from 'mixpanel-browser';
import { EVENTS } from '../../analytics/analytics-events';
import { DATE_FROM_PARAM, DATE_TO_PARAM, SEARCH_PARAM, useQueryParams } from 'shared/use-query-params';
import { useDateFormatter } from 'shared/utils';
import { DATE_FNS_GLOBAL_FORMAT } from 'shared/constants';
import { useFetchTags } from 'reporting/hooks/use-fetch-tag-options';
import { BaseReportType } from 'reporting/types';
import { ActivityTagFilter } from './activity-tag-filter';
import { DateRangePicker, type DateRangePickerProps, type DateRangeValue, type QuickSelectOption } from 'shared';
import { SearchBox } from 'shared/search-box';

export interface DateRange {
    from?: Date;
    to?: Date;
}
// Empty built-in preset list (show only our custom presets). Typed to the picker's
// own prop so it stays a valid built-in-id array rather than the misleading `never[]`.
const NO_BUILT_IN_QUICK_SELECTS: NonNullable<DateRangePickerProps['quickSelectOptions']> = [];

interface ActivitySideMenuFiltersProps {
    textFilter?: string;
    setTextFilter: (filter: string) => void;
    selectedDateRange: DateRange;
    setDateRange: (dates: DateRange) => void;
    tagFilters?: string[];
    setTagFilters: (tagFilters: string[]) => void;
    dataLoading: boolean;
    dateRangePlaceholder?: string;
    reportType?: BaseReportType;
}

export const ActivitySideMenuFilters: React.FC<ActivitySideMenuFiltersProps> = ({
    textFilter,
    setTextFilter,
    selectedDateRange,
    setDateRange,
    tagFilters = [],
    setTagFilters,
    dataLoading,
    dateRangePlaceholder,
    reportType,
}) => {
    const { __ } = useTranslations();
    const { items: tagItems, groupOrder, groupKeyMap, keyToGroupLabel, isLoading } = useFetchTags(tagFilters);
    const { removeQueryParam, updateQueryParam } = useQueryParams();
    const formatDate = useDateFormatter();

    const [searchText, setSearchText] = useState(textFilter ?? '');

    const presets = useMemo<QuickSelectOption[]>(
        () =>
            [7, 30].map((days) => ({
                id: `last-${days}-days`,
                label: __('misc.last_range_days', { range: String(days) }),
                getRange: (now?: Date): DateRangeValue => {
                    const ref = now ?? new Date();
                    return { start: subDays(ref, days), end: ref };
                },
            })),
        [__],
    );

    // Memoised so the value reference is stable across unrelated re-renders (e.g. search
    // keystrokes); a fresh object each render would re-sync the picker's staged state.
    const pickerValue = useMemo<DateRangeValue | undefined>(
        () =>
            selectedDateRange.from || selectedDateRange.to
                ? { start: selectedDateRange.from ?? undefined, end: selectedDateRange.to ?? undefined }
                : undefined,
        [selectedDateRange.from, selectedDateRange.to],
    );

    useEffect(() => {
        setTextFilter(searchText);
    }, [searchText, setTextFilter]);

    if (isLoading) {
        return <Spinner testId="side-menu-filter-spinner" />;
    }

    return (
        <>
            <div className="mb-4">
                <label htmlFor="activity-name-search" className="block mb-2">
                    <Text as="span" variant="label-large">
                        {`${__('misc.filter_by')} ${__('misc.name')}`}
                    </Text>
                </label>
                <SearchBox
                    id="activity-name-search"
                    value={searchText}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setSearchText(e.target.value);
                    }}
                    onDebouncedChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setDateRange({ from: null, to: null } as unknown as DateRange);
                        removeQueryParam(DATE_FROM_PARAM);
                        removeQueryParam(DATE_TO_PARAM);
                        updateQueryParam(SEARCH_PARAM, e.target.value);
                        mixpanel.track(EVENTS.sideMenu.activitySearch, {
                            searchDateRange: { from: null, to: null },
                            searchText: e.target.value,
                            searchFrom: 'Side Menu',
                            reportType,
                        });
                    }}
                    debounceTime={1000}
                    testId="activity-name-search"
                />
            </div>

            <div className="mb-4" style={{ maxWidth: '240px' }}>
                <label htmlFor="activity-tag-select" className="block mb-2">
                    <Text as="span" variant="label-large">
                        {`${__('misc.filter_by')} ${__('misc.tag')}`}
                    </Text>
                </label>
                <ActivityTagFilter
                    tagItems={tagItems}
                    groupOrder={groupOrder}
                    groupKeyMap={groupKeyMap}
                    keyToGroupLabel={keyToGroupLabel}
                    tagFilters={tagFilters}
                    setTagFilters={setTagFilters}
                    reportType={reportType}
                />
            </div>

            <div className="mb-4" style={{ maxWidth: '240px' }}>
                <Text variant="label-large" className="block mb-2">
                    {`${__('misc.filter_by')} ${__('misc.date')}`}
                </Text>
                <DateRangePicker
                    testId="activity-side-menu-date-range"
                    ariaLabel={`${__('misc.filter_by')} ${__('misc.date')}`}
                    triggerVariant="single"
                    placeholder={dateRangePlaceholder}
                    value={pickerValue}
                    minDate={new Date(2010, 0, 1)}
                    maxDate={new Date()}
                    quickSelectOptions={NO_BUILT_IN_QUICK_SELECTS}
                    customQuickSelectOptions={presets}
                    showTodayButton
                    disabled={dataLoading}
                    onChange={(range?: DateRangeValue) => {
                        const dates: DateRange = { from: range?.start, to: range?.end };
                        mixpanel.track(EVENTS.sideMenu.activitySearch, {
                            searchDateRange: dates,
                            searchText,
                            searchFrom: 'Side Menu',
                        });
                        setDateRange(dates);
                        updateQueryParam(
                            DATE_FROM_PARAM,
                            range?.start ? formatDate(range.start, DATE_FNS_GLOBAL_FORMAT) : 'null',
                        );
                        updateQueryParam(
                            DATE_TO_PARAM,
                            range?.end ? formatDate(range.end, DATE_FNS_GLOBAL_FORMAT) : 'null',
                        );
                    }}
                />
            </div>
        </>
    );
};
